import { nowIsoAndUnix } from "@@/shared/date-time/iso-and-unix";
import type { IconName } from "@@/shared/icon-name";
import { storageItemImageIdZodSchema } from "@@/shared/storage-items/storage-item-images/storage-item-image-id";
import { storageItemImageReferenceZodSchema } from "@@/shared/storage-items/storage-item-images/storage-item-image-reference";
import type {
    HexColor} from "@@/shared/stylable/hex-color/hex-color";
import {
    hexColorZodSchema,
} from "@@/shared/stylable/hex-color/hex-color";
import type {
    Translatable} from "@@/shared/translations/translation";
import {
    asTranslatableValue,
    translatableZodSchema,
    translation,
} from "@@/shared/translations/translation";
import { faShoePrints } from "@fortawesome/pro-regular-svg-icons";
import { z } from "zod";
import { pageBlockBaseZodSchema } from "../page-block-base";
import { pageBlockIdFactory } from "../page-block-id";
import { pageBlockTitleSizeZodSchema } from "../shared/page-block-title-size";

const footerPageBlockThemes = ["default"] as const;
const footerPageBlockThemesMeta: Record<
    (typeof footerPageBlockThemes)[number],
    {
        iconName: IconName;
        title: Translatable;
        description: Translatable;
    }
> = {
    default: {
        iconName: "fa-palette",
        title: translation({
            sv: "Standard",
            en: "Default",
        }),
        description: translation({
            sv: "Generellt standardtema",
            en: "General default theme",
        }),
    },
};

const footerPageBlockZodSchema = pageBlockBaseZodSchema.merge(
    z.object({
        type: z.literal("FOOTER_PAGE_BLOCK"),
        theme: z.enum(footerPageBlockThemes),
        // Texts
        title: translatableZodSchema,
        titleColor: hexColorZodSchema,
        subtitle: translatableZodSchema,
        subtitleColor: hexColorZodSchema,
        kicker: translatableZodSchema,
        kickerColor: hexColorZodSchema,
        textSize: pageBlockTitleSizeZodSchema,
        // Call to action
        ctaButtonEnabled: z.boolean(),
        ctaButtonText: translatableZodSchema.optional(),
        ctaButtonUrl: z.string().optional(),
        ctaButtonTextColor: hexColorZodSchema,
        ctaButtonBackgroundColor: hexColorZodSchema,
        ctaButtonBorderColor: hexColorZodSchema,
        // Content
        markdownText: translatableZodSchema.optional(),
        markdownTextColor: hexColorZodSchema,
        // Metadata
        metaDataTextColor: hexColorZodSchema.optional(),
        // Background
        backgroundColor: hexColorZodSchema,
        backgroundImage: z
            .union([
                storageItemImageReferenceZodSchema,
                storageItemImageIdZodSchema,
            ])
            .optional(),
    }),
);

type FooterPageBlock = z.infer<typeof footerPageBlockZodSchema>;

const footerPageBlockIcon = faShoePrints;
const _footerPageBlockFactoryParamsZodSchema = footerPageBlockZodSchema.omit({
    _id: true,
    _type: true,
    _createdAt: true,
    type: true,
});
type FactoryParams = z.infer<typeof _footerPageBlockFactoryParamsZodSchema>;
/**
 * Factory function for creating a new FooterPageBlock
 * @param {FactoryParams} params
 * @return {*}  {FooterPageBlock}
 */
const footerPageBlockFactory = (params: FactoryParams): FooterPageBlock => {
    return {
        _id: pageBlockIdFactory(),
        _type: "PAGE_BLOCK",
        _createdAt: nowIsoAndUnix(),
        type: "FOOTER_PAGE_BLOCK",
        pageId: params.pageId,
        theme: params.theme ?? "default",

        // Texts
        title: asTranslatableValue(params.title),
        titleColor: params.titleColor,
        subtitle: asTranslatableValue(params.subtitle),
        subtitleColor: params.titleColor,
        kicker: asTranslatableValue(params.kicker),
        kickerColor: params.titleColor,
        textSize: params.textSize,

        // Call to action
        ctaButtonEnabled: params.ctaButtonEnabled ?? false,
        ctaButtonText: asTranslatableValue(params.ctaButtonText),
        ctaButtonBackgroundColor: params.ctaButtonBackgroundColor,
        ctaButtonBorderColor: params.ctaButtonBorderColor,
        ctaButtonTextColor: params.ctaButtonTextColor,
        ctaButtonUrl: params.ctaButtonUrl,

        // Content
        markdownText: params.markdownText,
        markdownTextColor: params.markdownTextColor,

        // Metadata
        metaDataTextColor: params.metaDataTextColor ?? ("#888888" as HexColor),

        // Background
        backgroundColor: params.backgroundColor,
        backgroundImage: params.backgroundImage,
    };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isFooterPageBlock = (input: any): input is FooterPageBlock => {
    return input._type === "PAGE_BLOCK" && input.type === "FOOTER_PAGE_BLOCK";
};

export {
    footerPageBlockFactory,
    footerPageBlockIcon,
    footerPageBlockThemesMeta,
    footerPageBlockZodSchema,
    isFooterPageBlock,
};
export type { FooterPageBlock };
